import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Wrench, Save, PowerOff, Zap, Eye } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_TITLE = "Scheduled maintenance";
const DEFAULT_BODY = "We're rolling out an update. The Amino Exchange will be back online shortly — thanks for your patience.";

export default function MaintenanceCard() {
  const { settings, refreshSettings } = useSettings();
  const [enabled, setEnabled] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [eta, setEta] = useState("");
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    setEnabled(!!settings?.maintenance_enabled);
    setTitle(settings?.maintenance_title || "");
    setBody(settings?.maintenance_body || "");
    setEta(settings?.maintenance_eta || "");
  }, [settings]);

  const patch = async (payload) => {
    const { data } = await api.patch("/settings", payload);
    return data;
  };

  const saveMessaging = async () => {
    setSaving(true);
    try {
      await patch({
        maintenance_title: title.trim() || DEFAULT_TITLE,
        maintenance_body: body.trim() || DEFAULT_BODY,
        maintenance_eta: eta.trim(),
      });
      await refreshSettings();
      toast.success("Maintenance page updated");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (next) => {
    setToggling(true);
    try {
      await patch({ maintenance_enabled: next });
      await refreshSettings();
      toast.success(next ? "Site is now IN maintenance" : "Site is LIVE again");
      setEnabled(next);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setToggling(false);
    }
  };

  return (
    <Card
      className={`bg-white rounded-none p-6 border ${enabled ? "border-[#0D9488]" : "border-slate-200"}`}
      data-testid="maintenance-card"
    >
      <div className="flex items-center gap-3 mb-5">
        <div
          className="w-8 h-8 flex items-center justify-center"
          style={{ background: enabled ? "#0D9488" : "#E2E8F0" }}
        >
          <Wrench className={`w-4 h-4 ${enabled ? "text-black" : "text-slate-600"}`} />
        </div>
        <div>
          <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-slate-500">// Operations</div>
          <div className="font-display font-black uppercase tracking-tight text-xl">Maintenance Mode</div>
        </div>
        <div className="ml-auto">
          <span
            className={`inline-flex items-center gap-1.5 border px-2.5 py-1 font-mono-num text-[10px] uppercase tracking-widest ${
              enabled
                ? "border-[#0D9488] text-[#0D9488] bg-[#0D9488]/10"
                : "border-slate-200 text-slate-500"
            }`}
            data-testid="maintenance-status-chip"
          >
            <span className={`w-1.5 h-1.5 ${enabled ? "bg-[#0D9488]" : "bg-zinc-600"}`} />
            {enabled ? "ACTIVE — non-admins blocked" : "OFF — site is live"}
          </span>
        </div>
      </div>

      <p className="text-slate-600 text-sm mb-5 max-w-2xl leading-relaxed">
        When active, every non-admin user sees the maintenance screen instead of the app. Admins can still sign in and turn it off from here. Public sign-in and password-reset pages stay accessible so you never lock yourself out.
      </p>

      {/* Toggle */}
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <Button
          type="button"
          onClick={() => toggle(!enabled)}
          disabled={toggling}
          className={`rounded-none h-11 px-5 uppercase tracking-widest font-semibold ${
            enabled
              ? "bg-slate-50 hover:bg-slate-200 text-slate-900 border border-slate-300"
              : "bg-[#0D9488] hover:bg-[#0F766E] text-black"
          }`}
          data-testid="maintenance-toggle"
        >
          {enabled ? (
            <>
              <Zap className="w-4 h-4 mr-2 text-[#0D9488]" />
              {toggling ? "Deactivating…" : "Deactivate — bring site back online"}
            </>
          ) : (
            <>
              <PowerOff className="w-4 h-4 mr-2" />
              {toggling ? "Activating…" : "Activate maintenance mode"}
            </>
          )}
        </Button>
        <a
          href="/maintenance"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 h-11 px-4 border border-slate-200 hover:border-[#0D9488] hover:text-[#0D9488] text-slate-600 font-mono-num text-[11px] uppercase tracking-widest"
          data-testid="maintenance-preview"
        >
          <Eye className="w-3.5 h-3.5" /> Preview page
        </a>
      </div>

      {/* Message editor */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">Headline</Label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={DEFAULT_TITLE}
            maxLength={140}
            className="bg-slate-50 border-slate-200 rounded-none h-11"
            data-testid="maintenance-title-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">ETA (optional)</Label>
          <Input
            value={eta}
            onChange={(e) => setEta(e.target.value)}
            placeholder="e.g. Back by 8:00 PM ET"
            maxLength={140}
            className="bg-slate-50 border-slate-200 rounded-none h-11"
            data-testid="maintenance-eta-input"
          />
        </div>
        <div className="lg:col-span-2 space-y-1.5">
          <Label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-mono-num">
            Message
            <span className="ml-2 text-slate-400 normal-case tracking-normal">
              (supports **bold**, *italic*, [links](https://…), and line breaks)
            </span>
          </Label>
          <Textarea
            rows={5}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={DEFAULT_BODY}
            className="bg-slate-50 border-slate-200 rounded-none resize-none"
            data-testid="maintenance-body-input"
          />
        </div>
      </div>

      <div className="mt-5 flex justify-end">
        <Button
          type="button"
          onClick={saveMessaging}
          disabled={saving}
          className="h-11 px-6 rounded-none bg-[#0D9488] hover:bg-[#0F766E] text-black uppercase tracking-widest font-semibold"
          data-testid="maintenance-save"
        >
          <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save message"}
        </Button>
      </div>
    </Card>
  );
}
